import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Copy, Check, Share2, ChevronRight, Users, ExternalLink, Loader2, Inbox } from "lucide-react";
import { toast } from "sonner";
import { formatNumber, shortAddr } from "@/lib/web3";
import { useAuth } from "@/contexts/AuthContext";

const LEVEL_COLORS = [
  "#22C55E", "#3B82F6", "#A78BFA", "#F59E0B", "#EF4444",
  "#22D3EE", "#EC4899", "#F3BA2F", "#8B5CF6", "#10B981",
  "#FB7185", "#0EA5E9", "#84CC16", "#F97316", "#D946EF",
  "#06B6D4", "#F472B6", "#65A30D", "#EAB308", "#9333EA",
  "#EF4444", "#FBBF24", "#34D399", "#60A5FA", "#FCD34D",
];

export default function Team() {
  const { user } = useAuth();
  const [levels, setLevels] = useState([]);
  const [link, setLink] = useState("");
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [members, setMembers] = useState({}); // { [level]: { loading, data: [] } }

  useEffect(() => {
    (async () => {
      try {
        const [l, r] = await Promise.all([
          api.get("/team/stats"),
          api.get("/user/referral-link"),
        ]);
        setLevels(l.data.levels || []);
        const origin = (typeof window !== "undefined" && window.location?.origin) || "";
        const code = r.data.code || r.data.address;
        setLink(`${origin}/?ref=${code}`);
      } finally { setLoading(false); }
    })();
  }, []);

  const onCopy = async () => {
    await navigator.clipboard.writeText(link);
    setCopied(true);
    toast.success("Referral link copied");
    setTimeout(() => setCopied(false), 1500);
  };

  const onCopyAddr = async (addr) => {
    await navigator.clipboard.writeText(addr);
    toast.success("Address copied");
  };

  const fmtDate = (d) => {
    if (!d) return "—";
    try {
      const dt = new Date(d);
      return dt.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    } catch { return "—"; }
  };

  const toggleLevel = async (level) => {
    if (expanded === level) {
      setExpanded(null);
      return;
    }
    setExpanded(level);
    if (!members[level]) {
      setMembers(prev => ({ ...prev, [level]: { loading: true, data: [] } }));
      try {
        const r = await api.get(`/team/level-members/${level}`);
        setMembers(prev => ({ ...prev, [level]: { loading: false, data: r.data.members || [] } }));
      } catch (e) {
        setMembers(prev => ({ ...prev, [level]: { loading: false, data: [] } }));
      }
    }
  };

  const totalMembers = levels.reduce((s, l) => s + l.members, 0);
  const totalLayers = levels.filter(l => l.members > 0).length;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl sm:text-2xl font-semibold">My Team</h1>
        <p className="text-xs text-[var(--text-3)] mt-1">25-level downline tracking.</p>
      </div>

      {/* Referral link */}
      <div className="card-3d lift-on-hover p-4" data-testid="team-referral-card">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.14em] text-[var(--text-3)] mb-2"><Share2 size={12} /> Your referral link</div>
        <div className="flex gap-2">
          <Input data-testid="team-referral-link-input" readOnly value={link} className="bg-[var(--surface-1)] border-white/10 mono text-xs flex-1" />
          <Button data-testid="team-copy-referral-link-button" onClick={onCopy} className="font-semibold text-black" style={{ background: "linear-gradient(135deg, #FFD36A 0%, #F3BA2F 100%)" }}>
            {copied ? <Check size={14} className="mr-1" /> : <Copy size={14} className="mr-1" />} Copy
          </Button>
        </div>
      </div>

      {/* Top stats - dynamic colors */}
      <div className="grid grid-cols-3 gap-3">
        <div className="card-3d lift-on-hover kpi-gold p-4 text-center relative" data-testid="team-stat-members">
          <div className="shimmer-line" />
          <div className="text-[10px] uppercase tracking-[0.14em] text-[var(--text-3)]">Members</div>
          <div className="mono text-xl sm:text-2xl font-semibold mt-1" style={{ color: "#FFD36A", textShadow: "0 0 18px rgba(255,211,106,0.4)" }}>{totalMembers}</div>
        </div>
        <div className="card-3d lift-on-hover kpi-purple p-4 text-center relative" data-testid="team-stat-layers">
          <div className="shimmer-line" />
          <div className="text-[10px] uppercase tracking-[0.14em] text-[var(--text-3)]">Layers</div>
          <div className="mono text-xl sm:text-2xl font-semibold mt-1" style={{ color: "#C4B5FD", textShadow: "0 0 18px rgba(167,139,250,0.4)" }}>{totalLayers}</div>
        </div>
        <div className="card-3d lift-on-hover kpi-blue p-4 text-center relative" data-testid="team-stat-direct">
          <div className="shimmer-line" />
          <div className="text-[10px] uppercase tracking-[0.14em] text-[var(--text-3)]">Direct</div>
          <div className="mono text-xl sm:text-2xl font-semibold mt-1" style={{ color: "#93C5FD", textShadow: "0 0 18px rgba(96,165,250,0.4)" }}>{user?.direct_referrals || 0}</div>
        </div>
      </div>

      {/* Levels list */}
      <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--text-3)] px-1">All Levels</div>
      {loading ? <Skeleton className="h-64 bg-white/5" /> : (
        <div className="card-3d lift-on-hover divide-y divide-white/5" data-testid="team-levels-list">
          {levels.map((l, idx) => {
            const color = LEVEL_COLORS[idx % LEVEL_COLORS.length];
            const isOpen = expanded === l.level;
            return (
              <div key={l.level}>
                <button data-testid={`team-level-${l.level}`} onClick={() => toggleLevel(l.level)} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/[0.02]">
                  <div className="icon-pill-3d w-10 h-10 rounded-xl flex items-center justify-center mono text-sm font-semibold relative" style={{
                    background: `radial-gradient(circle at 30% 30%, ${color}AA 0%, ${color}33 60%, ${color}11 100%)`,
                    color: "#FFFFFF",
                    textShadow: `0 0 12px ${color}, 0 1px 0 rgba(0,0,0,0.4)`,
                    border: `1px solid ${color}66`,
                  }}>
                    {l.level}
                  </div>
                  <div className="flex-1 text-left">
                    <div className="text-sm font-semibold">{l.level === 1 ? "Direct" : `Level ${l.level}`}</div>
                    <div className="text-[11px] text-[var(--text-3)]">{l.members} members · {l.percent}%</div>
                  </div>
                  <ChevronRight size={16} className={`text-[var(--text-3)] transition-transform ${isOpen ? "rotate-90" : ""}`} />
                </button>
                {isOpen && (
                  <div className="px-4 pb-4 space-y-3">
                    {/* Business + Earned summary */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="umakant-panel p-3">
                        <div className="text-[10px] uppercase tracking-[0.14em] text-[var(--text-3)]">Business</div>
                        <div className="mono text-sm font-semibold">${formatNumber(l.business)}</div>
                      </div>
                      <div className="umakant-panel p-3">
                        <div className="text-[10px] uppercase tracking-[0.14em] text-[var(--text-3)]">Earned</div>
                        <div className="mono text-sm font-semibold" style={{ color: "#FFD36A" }}>${formatNumber(l.earned)}</div>
                      </div>
                    </div>

                    {/* Members list */}
                    <div className="text-[10px] uppercase tracking-[0.16em] text-[var(--text-3)] flex items-center gap-1.5 pt-1">
                      <Users size={11} /> Members in Level {l.level}
                    </div>

                    {!members[l.level] || members[l.level].loading ? (
                      <div className="flex items-center gap-2 py-4 px-3 text-xs text-[var(--text-3)]">
                        <Loader2 size={14} className="animate-spin" /> Loading members…
                      </div>
                    ) : members[l.level].data.length === 0 ? (
                      <div className="umakant-panel p-4 flex flex-col items-center gap-2 text-center">
                        <Inbox size={20} className="text-[var(--text-3)]" />
                        <div className="text-xs text-[var(--text-3)]">No members at this level yet.</div>
                      </div>
                    ) : (
                      <div className="space-y-1.5" data-testid={`team-level-${l.level}-members`}>
                        {members[l.level].data.map((m, mi) => {
                          const isActive = m.activated;
                          return (
                            <div
                              key={m.address}
                              className="relative px-2.5 py-2 rounded-lg overflow-hidden"
                              style={{
                                background: `linear-gradient(160deg, ${color}14 0%, var(--surface-1) 60%, var(--surface-2) 100%)`,
                                border: `1px solid ${color}3D`,
                                boxShadow: `inset 0 1px 0 rgba(255,255,255,0.06), 0 2px 6px -2px ${color}30`,
                              }}
                              data-testid={`team-member-${l.level}-${mi}`}
                            >
                              {/* Subtle top sheen */}
                              <div className="absolute top-0 left-0 right-0 h-1/2 pointer-events-none"
                                style={{ background: `linear-gradient(180deg, ${color}0F 0%, transparent 100%)` }} />

                              <div className="relative flex items-center gap-2">
                                {/* #N badge */}
                                <span
                                  className="mono text-[9px] font-bold px-1 py-px rounded shrink-0"
                                  style={{
                                    background: `linear-gradient(180deg, ${color}55 0%, ${color}22 100%)`,
                                    color: "#FFFFFF",
                                    border: `1px solid ${color}`,
                                    boxShadow: `inset 0 1px 0 rgba(255,255,255,0.25)`,
                                    textShadow: "0 1px 0 rgba(0,0,0,0.4)",
                                    minWidth: "22px",
                                    textAlign: "center",
                                  }}
                                >
                                  #{mi + 1}
                                </span>
                                {/* Name + Masked Email + Address */}

                                <button

                                  onClick={() => onCopyAddr(m.address)}

                                  className="flex flex-col items-start text-left hover:opacity-80 truncate flex-1 min-w-0"

                                  data-testid={`team-member-${l.level}-${mi}-address`}

                                  title={m.address}

                                >

                                  <span className="text-[11px] font-semibold text-[var(--text-1)] truncate max-w-full">{m.name || "Unnamed"}</span>

                                  {m.email ? (<span className="text-[9px] text-[var(--text-2)] truncate max-w-full">{(m.email.slice(0, 2) + "***" + (m.email.indexOf("@") > 0 ? m.email.slice(m.email.indexOf("@")) : ""))}</span>) : null}

                                  <span className="mono text-[9px] text-[var(--text-3)] truncate max-w-full">{shortAddr(m.address)}</span>

                                </button>
                                <Copy size={9} className="text-[var(--text-3)] shrink-0 cursor-pointer" onClick={() => onCopyAddr(m.address)} />
                                {/* Fund - inline highlight */}
                                <span
                                  className="ml-auto mono text-[11px] font-bold shrink-0"
                                  style={{ color: "#FFD36A", textShadow: "0 0 4px rgba(255,211,106,0.30)" }}
                                  title="Fund"
                                >
                                  ${formatNumber(m.total_deposit, { minimumFractionDigits: 0 })}
                                </span>
                                <a
                                  href={`https://bscscan.com/address/${m.address}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-[var(--text-3)] hover:text-[var(--purple-300)] shrink-0"
                                  title="View on BscScan"
                                >
                                  <ExternalLink size={9} />
                                </a>
                              </div>
                              {/* Sub row: Joined + Status */}
                              <div className="relative flex items-center justify-between mt-1 text-[9px]">
                                <span className="text-[var(--text-3)] mono">📅 {fmtDate(m.created_at)}</span>
                                <span
                                  className="inline-flex items-center gap-1 px-1.5 py-px rounded-full font-semibold uppercase tracking-[0.10em]"
                                  style={
                                    isActive
                                      ? { background: "rgba(34,197,94,0.14)", color: "#86EFAC", border: "1px solid rgba(34,197,94,0.32)" }
                                      : { background: "rgba(255,255,255,0.04)", color: "var(--text-3)", border: "1px solid var(--card3d-border)" }
                                  }
                                >
                                  <span className="w-1 h-1 rounded-full" style={{ background: isActive ? "#22C55E" : "var(--text-3)", boxShadow: isActive ? "0 0 4px #22C55E" : "none" }} />
                                  {isActive ? (m.package_name || m.rank || "Active") : "Inactive"}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
